import React from 'react'
import {useTranslation} from 'react-i18next'
import {ACTIVATION_STEPS, ActivationRoute, ActivationState, ActivationView, isFinalActivationState} from '../../domain/entities/Activation.ts'
import {daysToDeadline, EXPIRING_SOON_DAYS, touchpointLabelKey, useActivationFormatters} from './activationView.ts'

// Six dots, one per milestone reached. Reads milestones, not state: a skipped
// step stays empty (contract §1.4). The full text sits in aria-label/title.
export const ActivationStepper: React.FC<{activation: ActivationView}> = ({activation}) => {
    const {t} = useTranslation('dashboard')
    const reached = ACTIVATION_STEPS.map((step) => activation.milestones?.[step.milestone] != null)
    const done = reached.filter(Boolean).length
    const lastReached = reached.lastIndexOf(true)
    const description = t('activation.stepperLabel', {
        done,
        total: ACTIVATION_STEPS.length,
        step: lastReached >= 0 ? t(ACTIVATION_STEPS[lastReached].labelKey) : t('activation.step.none'),
    })
    const isClosed = activation.state === 'EXPIRED' || activation.state === 'CANCELLED'
    return (
        <span role="img" aria-label={description} title={description} className="inline-flex items-center gap-[3px]">
            {ACTIVATION_STEPS.map((step, i) => (
                <span key={step.milestone}
                      className={`h-1.5 w-[14px] rounded-full ${reached[i]
                          ? (isClosed ? 'bg-neutralBlack-500' : i === ACTIVATION_STEPS.length - 1 ? 'bg-[#12734A]' : 'bg-neutralBlack-900')
                          : 'bg-neutralBlack-200'}`}/>
            ))}
        </span>
    )
}

const ROUTE_CLASSES: Record<ActivationRoute, string> = {
    A: 'bg-[#E8EEFC] text-[#2C4BAA] border-[#2C4BAA]/[.18]',
    B: 'bg-orange-50 text-orange-800 border-orange-700/[.18]',
    C1: 'bg-[#EEF8F2] text-[#16603F] border-[#16603F]/[.18]',
}

export const ActivationRouteBadge: React.FC<{route: ActivationRoute}> = ({route}) => {
    const {t} = useTranslation('dashboard')
    return (
        <span title={t(`activation.route.${route}Help`)}
              className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[11px] font-semibold whitespace-nowrap ${ROUTE_CLASSES[route] ?? ROUTE_CLASSES.A}`}>
            {t('activation.route.badge', {route})}
        </span>
    )
}

const STATE_CLASSES: Partial<Record<ActivationState, string>> = {
    ACTIVATED: 'bg-[#E3F3EA] text-[#12734A]',
    EXPIRED: 'bg-neutralBlack-100 text-neutralBlack-700',
    CANCELLED: 'bg-neutralBlack-100 text-neutralBlack-700',
}

export const ActivationStatePill: React.FC<{state: ActivationState}> = ({state}) => {
    const {t} = useTranslation('dashboard')
    return (
        <span className={`inline-flex items-center h-5 px-2 rounded-full text-[11.5px] font-medium whitespace-nowrap ${STATE_CLASSES[state] ?? 'bg-[#F1F1F4] text-neutralBlack-900'}`}>
            {t(`activation.state.${state}`)}
        </span>
    )
}

// "Caduca en 143 días" — orange inside the 30-day window, red once past.
export const ActivationCountdown: React.FC<{activation: ActivationView}> = ({activation}) => {
    const {t} = useTranslation('dashboard')
    const {formatDay} = useActivationFormatters()
    if (activation.state === 'ACTIVATED' || activation.state === 'CANCELLED') return null
    if (activation.state === 'EXPIRED') {
        return <span className="text-[12px] text-neutralBlack-600 whitespace-nowrap">{t('activation.deadline.expiredOn', {date: formatDay(activation.redemptionDeadline)})}</span>
    }
    const days = daysToDeadline(activation)
    if (days == null) return null
    const tone = days <= 0 ? 'text-[#B3261E] font-semibold' : days <= EXPIRING_SOON_DAYS ? 'text-orange-700 font-semibold' : 'text-neutralBlack-600'
    return (
        <span title={formatDay(activation.redemptionDeadline)} className={`text-[12px] whitespace-nowrap ${tone}`}>
            {days <= 0 ? t('activation.deadline.today') : t('activation.deadline.inDays', {count: days})}
        </span>
    )
}

export const ContactFailedFlag: React.FC = () => {
    const {t} = useTranslation('dashboard')
    return (
        <span title={t('activation.contactFailedHelp')}
              className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-[#FDECEC] text-[11px] font-semibold text-[#B3261E] whitespace-nowrap">
            <span aria-hidden="true">!</span>
            {t('activation.contactFailed')}
        </span>
    )
}

// "En pausa hasta el 12 nov": automatic reminders are paused until that date.
// Renders nothing once the date has passed (or when there's no pause).
export const PausedUntilFlag: React.FC<{activation: ActivationView}> = ({activation}) => {
    const {t} = useTranslation('dashboard')
    const {formatDay} = useActivationFormatters()
    const pausedUntil = activation.flags?.pausedUntil
    if (pausedUntil == null || pausedUntil <= Date.now()) return null
    return (
        <span title={t('activation.pausedUntilHelp')}
              className="inline-flex items-center h-5 px-1.5 rounded-md bg-[#F1F1F4] text-[11px] font-semibold text-neutralBlack-800 whitespace-nowrap">
            {t('activation.pausedUntil', {date: formatDay(pausedUntil)})}
        </span>
    )
}

// "Próximo: Email al viajero · 12 nov" and "Último: Entregado".
export const ActivationTouches: React.FC<{activation: ActivationView}> = ({activation}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useActivationFormatters()
    const {next, lastTouch} = activation
    const isOpen = !isFinalActivationState(activation.state)
    if (!(isOpen && next) && !lastTouch) return null
    return (
        <span className="flex flex-col gap-0.5 text-[11.5px] leading-[1.4] text-neutralBlack-600 min-w-0">
            {isOpen && next && (
                <span className="truncate">
                    <span className="font-medium text-neutralBlack-900">{t('activation.next')}</span>{' '}
                    {t(touchpointLabelKey(next.touchpointKey), {defaultValue: next.touchpointKey})} · {formatDateTime(next.at)}
                </span>
            )}
            {lastTouch && (
                <span className="truncate">
                    <span className="font-medium text-neutralBlack-900">{t('activation.last')}</span>{' '}
                    {t(touchpointLabelKey(lastTouch.touchpointKey), {defaultValue: lastTouch.touchpointKey})} · {t(`activation.touchStatus.${lastTouch.status}`, {defaultValue: lastTouch.status})}
                </span>
            )}
        </span>
    )
}

// Everything the pack row shows about one activation, stacked in one cell.
export const ActivationSummary: React.FC<{activation: ActivationView; actions?: React.ReactNode}> = ({activation, actions}) => (
    <span className="flex flex-col gap-1.5 items-start min-w-0">
        <span className="flex items-center gap-2 flex-wrap">
            <ActivationStepper activation={activation}/>
            <ActivationRouteBadge route={activation.route}/>
            {activation.flags?.contactFailed && <ContactFailedFlag/>}
            <PausedUntilFlag activation={activation}/>
            {actions}
        </span>
        <span className="flex items-center gap-2 flex-wrap">
            <ActivationStatePill state={activation.state}/>
            <ActivationCountdown activation={activation}/>
        </span>
        <ActivationTouches activation={activation}/>
    </span>
)
